import React, { useState, useRef } from "react";
import axios from "axios";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

import { FaSpinner } from "react-icons/fa";

import MultipleChoice from "./forms/MultipleChoice";
import TextArea from "./forms/TextArea";
import Radio from "./forms/Radio";
import TextField from "./forms/TextField";
import SectionTitle from "./forms/SectionTitle";
import RatingScale from "./forms/RatingScale";

const EMAIL_KEY = process.env.EMAIL_KEY || "kieron123";

function DynamicFormContent({ components, title }) {
  const form = useRef();
  const submitting = useRef(false);
  const lastSubmissionTime = useRef(0);
  const [website, setWebsite] = useState("");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [error, setError] = useState("");
  const [data, setData] = useState({
    formTitle: title,
  });
  const [formSuccess, setFormSuccess] = useState(false);
  const [loading, setLoading] = useState();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current || !form.current.reportValidity() || website) return;
    setError("");
    if (Date.now() - lastSubmissionTime.current < 30000) {
      setError("Please wait 30 seconds before submitting again.");
      return;
    }
    if (
      !process.env.GATSBY_RECAPTCHA_SITE_KEY ||
      !executeRecaptcha ||
      !window.grecaptcha
    ) {
      setError("Security verification is loading. Please try again shortly.");
      return;
    }
    submitting.current = true;
    setLoading(true);
    try {
      const recaptchaToken = await executeRecaptcha("submit_form");
      if (!recaptchaToken) throw new Error("Security verification failed");
      lastSubmissionTime.current = Date.now();
      const result = await axios.post("/api/email", {
        data,
        EMAIL_KEY,
        website,
        recaptchaToken,
      });
      if (result.status === 200) setFormSuccess(true);
    } catch (error) {
      setError(
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
          "Unable to submit your form. Please try again later."
      );
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      {!formSuccess ? (
        <form
          className={`form__form ${error.length >= 1 ? "error" : ""}`}
          onSubmit={handleSubmit}
          ref={form}
        >
          {components.map((comp, index) => {
            switch (comp.__typename) {
              case "StrapiQuery_ComponentFormsMultipleChoice":
                return (
                  <MultipleChoice
                    key={index}
                    input={comp}
                    question={index}
                    setData={setData}
                  />
                );
              case "StrapiQuery_ComponentFormsTextArea":
                return (
                  <TextArea
                    key={index}
                    input={comp}
                    question={index}
                    setData={setData}
                  />
                );
              case "StrapiQuery_ComponentFormsRadio":
                return (
                  <Radio
                    key={index}
                    input={comp}
                    question={index}
                    setData={setData}
                  />
                );
              case "StrapiQuery_ComponentFormsTextField":
                return (
                  <TextField
                    key={index}
                    input={comp}
                    question={index}
                    setData={setData}
                  />
                );
              case "StrapiQuery_ComponentFormsSectionTitle":
                return (
                  <SectionTitle
                    key={index}
                    input={comp}
                    question={index}
                    setData={setData}
                  />
                );
              case "StrapiQuery_ComponentFormsRatingScale":
                return (
                  <RatingScale
                    key={index}
                    input={comp}
                    question={index}
                    setData={setData}
                  />
                );
              default:
                return null;
            }
          })}
          <div style={{ display: "none" }} aria-hidden="true">
            <input
              type="text"
              name="website"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          {error && (
            <div className="form__error-container" role="alert">
              <span className="form__error-message">{error}</span>
            </div>
          )}
          <button className="form__submit" type="submit" disabled={loading}>
            Send
          </button>

          {loading && (
            <div className="form__loading-container">
              <div className="form__loading-background"></div>
              <div className="form__loading-text-container">
                <FaSpinner className="loader" />
                <h3 className="form__loading-text">Processing Form</h3>
              </div>
            </div>
          )}
        </form>
      ) : (
        <p className="form__success">Thank you for submitting the form.</p>
      )}
    </>
  );
}

export default function DynamicForm(props) {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.GATSBY_RECAPTCHA_SITE_KEY}
    >
      <DynamicFormContent {...props} />
    </GoogleReCaptchaProvider>
  );
}
